import { useState } from 'react';
import { Check, ClipboardCopy, Info } from 'lucide-react';
import { useVatLang } from './VatLang';
import type { AnnualLineValue, AnnualReturnResponse } from '../../types/vatReporting';

/**
 * The Erfassungshilfe - the annual figures laid out the way Mein ELSTER asks for them.
 *
 * WHAT IT REPLACES. The last sheet of Heike's annual workbook was a hand-built copy of the
 * form: line numbers down the left, the figure to type on the right, used as a script while
 * keying the Jahreserklaerung into ELSTER. This is that sheet, generated - and generated for
 * the YEAR'S OWN FORM, because the printed line numbers move between years (2024: Zeilen
 * 102-119, 2025: 103-120). Everything the app stores uses the stable line key; only this
 * panel and the form panel resolve the printed number.
 *
 * TWO GROUPS, AND THE SPLIT IS THE WHOLE POINT. ELSTER computes the sums itself, so keying a
 * figure into a computed field is impossible and comparing against it is the only check that
 * the typed fields landed in the right boxes. The panel therefore says which is which instead
 * of presenting nineteen figures as nineteen keystrokes.
 *
 * NO SORTING, DELIBERATELY. The order IS the content - it mirrors the paper form, and a table
 * you can reorder would stop being a script to read top to bottom. Same reasoning as the
 * matrix and the form panel; the drillable lists on this tab follow the full table standard.
 */

interface Props {
    data: AnnualReturnResponse;
}

/** Bases are whole euro in the form; tax amounts carry cents. */
const isWholeEuro = (line: AnnualLineValue) => line.kind === 'base';

/** Exactly what goes into the ELSTER field: German decimal comma, no thousands separator. */
function typeable(line: AnnualLineValue): string | null {
    if (line.reportedValue === null) return null;
    return isWholeEuro(line)
        ? String(Math.trunc(line.reportedValue))
        : line.reportedValue.toFixed(2).replace('.', ',');
}

/** What the panel prints - grouped for reading, unlike the value that gets copied. */
function display(line: AnnualLineValue): string {
    if (line.reportedValue === null) return '—';
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: isWholeEuro(line) ? 0 : 2,
        maximumFractionDigits: isWholeEuro(line) ? 0 : 2,
    }).format(line.reportedValue);
}

export function ElsterEntryHelp({ data }: Props) {
    const { t } = useVatLang();
    const [copied, setCopied] = useState<string | null>(null);

    const copy = async (key: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            window.setTimeout(() => setCopied(c => (c === key ? null : c)), 1500);
        } catch {
            // A browser that refuses the clipboard is not an error worth a banner - the figure
            // is on screen and can be read off it, which is what the panel is for.
        }
    };

    const toType = data.lines.filter(l => l.elster === 'type');
    const computed = data.lines.filter(l => l.elster === 'computed');

    const asText = (lines: AnnualLineValue[]) => lines
        .map(l => [
            l.formLineNo !== null ? `Zeile ${l.formLineNo}` : '',
            l.kennzahl ? `Kz ${l.kennzahl}` : '',
            l.label,
            typeable(l) ?? '',
        ].join('\t'))
        .join('\n');

    const row = (line: AnnualLineValue, group: 'type' | 'computed') => {
        const value = typeable(line);
        const key = `${group}-${line.key}`;
        return (
            <tr key={line.key} className="hover:bg-gray-50">
                <td className="px-4 py-1.5 whitespace-nowrap font-medium text-gray-900 w-20">
                    {line.formLineNo !== null ? `Z. ${line.formLineNo}` : '—'}
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-gray-400 w-16">
                    {line.kennzahl ? `Kz ${line.kennzahl}` : ''}
                </td>
                <td className="px-2 py-1.5 text-gray-700">
                    {line.label}
                    {line.note && <span className="ml-1.5 text-xs text-amber-700">({line.note})</span>}
                </td>
                <td className={`px-4 py-1.5 text-right whitespace-nowrap tabular-nums ${
                    group === 'type' ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                    {display(line)}
                </td>
                <td className="px-2 py-1.5 w-10">
                    {value !== null && (
                        <button onClick={() => copy(key, value)}
                                title={t('elster.copyValue', { value })}
                                className="text-gray-400 hover:text-blue-600">
                            {copied === key
                                ? <Check className="h-3.5 w-3.5 text-green-600" />
                                : <ClipboardCopy className="h-3.5 w-3.5" />}
                        </button>
                    )}
                </td>
            </tr>
        );
    };

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('elster.title', { year: data.year })}
                    <span className="ml-2 font-normal text-gray-500">{t('elster.subtitle')}</span>
                </h3>
                <button onClick={() => copy('all', asText([...toType, ...computed]))}
                        className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800">
                    {copied === 'all'
                        ? <Check className="h-3.5 w-3.5 text-green-600" />
                        : <ClipboardCopy className="h-3.5 w-3.5" />}
                    {t('elster.copyAll')}
                </button>
            </div>

            <table className="w-full text-sm">
                <tbody>
                    <tr className="bg-blue-50/60 border-b border-blue-100">
                        <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold text-blue-900">
                            {t('elster.groupType')}
                        </td>
                    </tr>
                    {toType.map(l => row(l, 'type'))}
                    <tr className="bg-gray-50 border-y">
                        <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold text-gray-600">
                            {t('elster.groupComputed')}
                        </td>
                    </tr>
                    {computed.map(l => row(l, 'computed'))}
                </tbody>
            </table>

            <div className="px-4 py-2 border-t text-xs text-gray-500 flex items-start gap-1.5">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{t('elster.legend')}</span>
            </div>
        </div>
    );
}
